import { lazy, Suspense, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Globe2,
  Lock,
  Trash2,
  Archive,
  Upload,
  FileSpreadsheet,
  FileText,
  Download,
  Pencil,
  UserPlus,
  X,
} from "lucide-react";
import {
  useWorkbench,
  useUpdateWorkbench,
  useArchiveWorkbench,
  useDeleteWorkbench,
  useWorkbenchFiles,
  useUploadWorkbenchFile,
  useGenerateBlankExcel,
  useGenerateBlankWord,
  useDeleteWorkbenchFile,
  useAddWorkbenchMember,
  useRemoveWorkbenchMember,
  useWorkbenchDirectory,
  downloadWorkbenchFile,
  formatFileSize,
} from "@/api/workbenches";
import { getApiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";

// Editores pesados (exceljs/mammoth/docx/tiptap) cargados solo cuando se abren, para no inflar el bundle inicial de toda la app.
const ExcelEditorModal = lazy(() => import("./ExcelEditorModal"));
const WordEditorModal = lazy(() => import("./WordEditorModal"));

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export default function WorkbenchDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const currentUser = useAuthStore((s) => s.user);

  const { data, isLoading } = useWorkbench(id);
  const { data: files } = useWorkbenchFiles(id);

  const updateWorkbench = useUpdateWorkbench();
  const archiveWorkbench = useArchiveWorkbench();
  const deleteWorkbench = useDeleteWorkbench();
  const uploadFile = useUploadWorkbenchFile();
  const generateExcel = useGenerateBlankExcel();
  const generateWord = useGenerateBlankWord();
  const deleteFile = useDeleteWorkbenchFile();
  const addMember = useAddWorkbenchMember();
  const removeMember = useRemoveWorkbenchMember();

  const [editingDescription, setEditingDescription] = useState(false);
  const [description, setDescription] = useState("");
  const [showAddMember, setShowAddMember] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const { data: directory } = useWorkbenchDirectory(memberSearch);
  const [editingFile, setEditingFile] = useState<{ id: string; name: string; kind: "xlsx" | "docx" } | null>(null);

  if (isLoading) return <p className="text-sm text-gray-400">Cargando...</p>;
  if (!data) return <p className="text-sm text-gray-400">Mesa de trabajo no encontrada.</p>;

  const { workbench, members } = data;
  const isOwner = members.some((m) => m.userId === currentUser?.id && m.role === "PROPIETARIO");

  const handleArchive = async () => {
    if (!window.confirm(`¿Archivar la mesa "${workbench.name}"?`)) return;
    try {
      await archiveWorkbench.mutateAsync(workbench.id);
      toast.success("Mesa de trabajo archivada.");
      navigate("/workbenches");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar la mesa "${workbench.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await deleteWorkbench.mutateAsync(workbench.id);
      toast.success("Mesa de trabajo eliminada.");
      navigate("/workbenches");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await uploadFile.mutateAsync({ workbenchId: workbench.id, file });
      toast.success("Archivo subido correctamente.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      e.target.value = "";
    }
  };

  const handleGenerateExcel = async () => {
    try {
      await generateExcel.mutateAsync({ workbenchId: workbench.id });
      toast.success("Libro de Excel en blanco generado.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleGenerateWord = async () => {
    try {
      await generateWord.mutateAsync(workbench.id);
      toast.success("Documento de Word en blanco generado.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleDownload = async (fileId: string) => {
    try {
      const { url } = await downloadWorkbenchFile(workbench.id, fileId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleDeleteFile = async (fileId: string, name: string) => {
    if (!window.confirm(`¿Eliminar el archivo "${name}"?`)) return;
    try {
      await deleteFile.mutateAsync({ workbenchId: workbench.id, fileId });
      toast.success("Archivo eliminado.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleAddMember = async (userId: string) => {
    try {
      await addMember.mutateAsync({ workbenchId: workbench.id, userId });
      toast.success("Miembro agregado correctamente.");
      setShowAddMember(false);
      setMemberSearch("");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleRemoveMember = async (userId: string, name: string) => {
    if (!window.confirm(`¿Quitar a "${name}" de la mesa?`)) return;
    try {
      await removeMember.mutateAsync({ workbenchId: workbench.id, userId });
      toast.success("Miembro removido.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const saveDescription = async () => {
    try {
      await updateWorkbench.mutateAsync({ id: workbench.id, description });
      toast.success("Descripción actualizada.");
      setEditingDescription(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Link to="/workbenches" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft size={14} /> Mesa de Trabajo
        </Link>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-gray-900">{workbench.name}</h1>
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                workbench.visibility === "PUBLICA" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"
              }`}
            >
              {workbench.visibility === "PUBLICA" ? <Globe2 size={12} /> : <Lock size={12} />}
              {workbench.visibility === "PUBLICA" ? "Pública" : "Privada"}
            </span>
            {workbench.status === "ARCHIVADA" && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-400">Archivada</span>
            )}
          </div>
          {isOwner && workbench.status === "ACTIVA" && (
            <div className="flex gap-2">
              <button type="button" className="btn-secondary flex items-center gap-1.5" onClick={handleArchive}>
                <Archive size={14} /> Archivar
              </button>
              <button type="button" className="btn-secondary flex items-center gap-1.5 text-red-600" onClick={handleDelete}>
                <Trash2 size={14} /> Eliminar
              </button>
            </div>
          )}
        </div>

        {editingDescription ? (
          <div className="mt-2 flex items-start gap-2">
            <textarea className="input-field flex-1" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
            <button type="button" className="btn-primary" onClick={saveDescription}>
              Guardar
            </button>
            <button type="button" className="btn-secondary" onClick={() => setEditingDescription(false)}>
              Cancelar
            </button>
          </div>
        ) : (
          <p
            className={`mt-2 text-sm text-gray-500 ${isOwner ? "cursor-pointer hover:text-gray-700" : ""}`}
            onClick={() => {
              if (!isOwner) return;
              setDescription(workbench.description ?? "");
              setEditingDescription(true);
            }}
          >
            {workbench.description || (isOwner ? "Agregar una descripción..." : "Sin descripción.")}
          </p>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-gray-900">Archivos</h2>
            {hasPermission("workbenches.create") && workbench.status === "ACTIVA" && (
              <div className="flex flex-wrap gap-2">
                <label className="btn-secondary flex cursor-pointer items-center gap-1.5">
                  <Upload size={14} /> Subir archivo
                  <input type="file" className="hidden" onChange={handleUpload} disabled={uploadFile.isPending} />
                </label>
                <button type="button" className="btn-secondary flex items-center gap-1.5" onClick={handleGenerateExcel} disabled={generateExcel.isPending}>
                  <FileSpreadsheet size={14} /> Nuevo Excel
                </button>
                <button type="button" className="btn-secondary flex items-center gap-1.5" onClick={handleGenerateWord} disabled={generateWord.isPending}>
                  <FileText size={14} /> Nuevo Word
                </button>
              </div>
            )}
          </div>

          <div className="card divide-y divide-gray-100">
            {!files || files.length === 0 ? (
              <p className="p-6 text-center text-sm text-gray-400">No hay archivos en esta mesa todavía.</p>
            ) : (
              files.map((file) => (
                <div key={file.id} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">{file.originalName}</p>
                    <p className="text-xs text-gray-400">
                      {formatFileSize(file.sizeBytes)} · {file.origin === "GENERADO" ? "Generado" : "Subido"} por{" "}
                      {file.uploadedBy?.fullName ?? "—"}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    {hasPermission("workbenches.update") && workbench.status === "ACTIVA" && (file.mimeType === XLSX_MIME || file.mimeType === DOCX_MIME) && (
                      <button
                        type="button"
                        className="rounded-md p-1.5 text-gray-400 hover:bg-gray-50 hover:text-brand-600"
                        onClick={() => setEditingFile({ id: file.id, name: file.originalName, kind: file.mimeType === XLSX_MIME ? "xlsx" : "docx" })}
                        title="Editar en el sistema"
                      >
                        <Pencil size={16} />
                      </button>
                    )}
                    {hasPermission("workbenches.download") && (
                      <button type="button" className="rounded-md p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-700" onClick={() => handleDownload(file.id)} title="Descargar">
                        <Download size={16} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="rounded-md p-1.5 text-gray-400 hover:bg-gray-50 hover:text-red-600"
                      onClick={() => handleDeleteFile(file.id, file.originalName)}
                      title="Eliminar"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Miembros</h2>
            {isOwner && (
              <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-700" onClick={() => setShowAddMember((v) => !v)}>
                <UserPlus size={14} className="inline" /> Agregar
              </button>
            )}
          </div>

          {showAddMember && (
            <div className="relative">
              <input
                className="input-field"
                placeholder="Buscar por nombre o correo..."
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
              />
              {memberSearch && directory && directory.length > 0 && (
                <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                  {directory
                    .filter((u) => !members.some((m) => m.userId === u.id))
                    .map((u) => (
                      <button
                        type="button"
                        key={u.id}
                        className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                        onClick={() => handleAddMember(u.id)}
                      >
                        {u.fullName}
                        <span className="ml-1 text-xs text-gray-400">{u.email}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
          )}

          <div className="card divide-y divide-gray-100">
            {members.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-2 px-4 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm text-gray-900">{m.user?.fullName ?? "—"}</p>
                  <p className="truncate text-xs text-gray-400">{m.user?.email}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${m.role === "PROPIETARIO" ? "bg-brand-100 text-brand-700" : "bg-gray-100 text-gray-600"}`}>
                    {m.role === "PROPIETARIO" ? "Propietario" : "Miembro"}
                  </span>
                  {isOwner && m.role !== "PROPIETARIO" && (
                    <button type="button" className="text-gray-400 hover:text-red-600" onClick={() => handleRemoveMember(m.userId, m.user?.fullName ?? "")}>
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {workbench.visibility === "PUBLICA" && (
            <p className="text-xs text-gray-400">
              Al ser pública, todo el personal con acceso a Mesa de Trabajo puede verla; los miembros listados aquí son solo informativos.
            </p>
          )}
        </div>
      </div>

      {editingFile && (
        <Suspense fallback={<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 text-sm text-white">Cargando editor...</div>}>
          {editingFile.kind === "xlsx" ? (
            <ExcelEditorModal workbenchId={workbench.id} fileId={editingFile.id} fileName={editingFile.name} onClose={() => setEditingFile(null)} />
          ) : (
            <WordEditorModal workbenchId={workbench.id} fileId={editingFile.id} fileName={editingFile.name} onClose={() => setEditingFile(null)} />
          )}
        </Suspense>
      )}
    </div>
  );
}
